import { normalizeMock } from './workspaceTabPersistence';
import { protocolFieldsOf } from './requestProtocolFields';

/**
 * Resolve saved requests for editor open, duplicate, and shortcuts.
 * Prefers live tab state, then sidebar collection records; optional API fetch fills cloud gaps.
 */

export function findSidebarRequest(collections, requestId) {
  if (requestId == null) return null;
  for (const col of collections || []) {
    const found = (col.requests || []).find((r) => String(r.id) === String(requestId));
    if (found) return found;
  }
  return null;
}

/** Find the id of the collection that directly owns a given request. */
export function findParentCollectionId(collections, requestId) {
  if (!requestId) return null;
  const parent = (collections || []).find((c) =>
    (c.requests || []).some((r) => String(r.id) === String(requestId))
  );
  return parent?.id ?? null;
}

/** Resolve the collection id a request tab belongs to (explicit, else by saved request). */
export function resolveRequestTabCollectionId(tab, collections) {
  if (!tab) return null;
  if (tab.collectionId) return tab.collectionId;
  if (tab.originalId) return findParentCollectionId(collections, tab.originalId);
  return null;
}

export function tabMatchesRequest(tab, requestId) {
  if (!tab || requestId == null) return false;
  return (
    (tab.originalId != null && String(tab.originalId) === String(requestId)) ||
    (String(tab.id) === String(requestId) && !tab.isNewRequest)
  );
}

/** Higher score = more complete editor state. */
export function scoreTabCompleteness(tab) {
  if (!tab) return 0;
  let score = 0;
  if ((tab.url || '').trim()) score += 4;
  if ((tab.headers || []).some((h) => (h?.key || '').trim())) score += 2;
  if ((tab.params || []).some((p) => (p?.key || '').trim())) score += 1;
  if ((tab.body || '').trim()) score += 1;
  if ((tab.mock?.responseExamples || []).length > 0) score += 2;
  if (!tab.isNewRequest) score += 1;
  return score;
}

function hasMockExamples(mock) {
  return Array.isArray(mock?.responseExamples) && mock.responseExamples.length > 0;
}

function pickMockField(tab, record, preferTab) {
  if (preferTab && hasMockExamples(tab?.mock)) return normalizeMock(tab.mock);
  if (hasMockExamples(record?.mock)) return normalizeMock(record.mock);
  if (hasMockExamples(tab?.mock)) return normalizeMock(tab.mock);
  return normalizeMock(record?.mock ?? tab?.mock);
}

/**
 * When multiple tabs reference the same saved request, prefer the active tab,
 * then the tab with the richest editor fields.
 */
export function findRequestTabForRequest(tabs, requestId, activeRequestTabId = null) {
  const matches = (tabs || []).filter((t) => tabMatchesRequest(t, requestId));
  if (!matches.length) return null;
  if (matches.length === 1) return matches[0];

  return [...matches].sort((a, b) => {
    const aActive =
      activeRequestTabId != null && String(a.id) === String(activeRequestTabId) ? 1000 : 0;
    const bActive =
      activeRequestTabId != null && String(b.id) === String(activeRequestTabId) ? 1000 : 0;
    return bActive + scoreTabCompleteness(b) - (aActive + scoreTabCompleteness(a));
  })[0];
}

function hasKvKeys(rows) {
  return Array.isArray(rows) && rows.some((r) => (r?.key || '').trim());
}

function pickKvField(tab, record, field, preferTab) {
  // Empty [] is truthy but must not override API/sidebar rows that have real keys.
  if (preferTab && hasKvKeys(tab?.[field])) return tab[field];
  if (hasKvKeys(record?.[field])) return record[field];
  if (hasKvKeys(tab?.[field])) return tab[field];
  return record?.[field] ?? tab?.[field];
}

function pickScalar(tab, record, field, preferTab) {
  if (preferTab && tab?.[field] != null && tab[field] !== '') return tab[field];
  if (record?.[field] != null && record[field] !== '') return record[field];
  return tab?.[field] ?? record?.[field];
}

/** Overlay live tab fields onto a collection/API record (unsaved edits win). */
export function mergeRequestSources(sidebarRecord, tab) {
  if (!sidebarRecord && !tab) return null;
  if (!tab) return sidebarRecord ? { ...sidebarRecord } : null;
  if (!sidebarRecord) {
    return {
      id: tab.originalId ?? tab.id,
      name: tab.name,
      method: tab.method,
      url: tab.url,
      headers: tab.headers,
      params: tab.params,
      cookies: tab.cookies,
      body: tab.body,
      bodyType: tab.bodyType,
      rawBodyLanguage: tab.rawBodyLanguage,
      auth: tab.auth,
      description: tab.description,
      folder_id: tab.folderId ?? tab.folder_id,
      settings: tab.settings,
      preRequestScript: tab.preRequestScript ?? '',
      testScript: tab.testScript ?? '',
      formDataFields: tab.formDataFields,
      mock: normalizeMock(tab.mock),
      type: tab.type || 'http',
      ...protocolFieldsOf({ ...tab, type: tab.type || 'http' }),
    };
  }

  const preferTab = Boolean(tab.hasUnsavedChanges);

  return {
    ...sidebarRecord,
    method: pickScalar(tab, sidebarRecord, 'method', preferTab),
    url: pickScalar(tab, sidebarRecord, 'url', preferTab),
    name: pickScalar(tab, sidebarRecord, 'name', preferTab),
    headers: pickKvField(tab, sidebarRecord, 'headers', preferTab),
    params: pickKvField(tab, sidebarRecord, 'params', preferTab),
    cookies: pickKvField(tab, sidebarRecord, 'cookies', preferTab),
    body: pickScalar(tab, sidebarRecord, 'body', preferTab),
    bodyType: pickScalar(tab, sidebarRecord, 'bodyType', preferTab),
    // sidebarRecord has no rawBodyLanguage (server stores body_type, not language);
    // always prefer whatever the tab has so user's choice survives re-selection.
    rawBodyLanguage: tab.rawBodyLanguage ?? sidebarRecord.rawBodyLanguage,
    auth: preferTab && tab.auth != null ? tab.auth : sidebarRecord.auth ?? tab.auth,
    preRequestScript: pickScalar(tab, sidebarRecord, 'preRequestScript', preferTab) ?? '',
    testScript: pickScalar(tab, sidebarRecord, 'testScript', preferTab) ?? '',
    formDataFields:
      preferTab && tab.formDataFields != null
        ? tab.formDataFields
        : sidebarRecord.formDataFields ?? tab.formDataFields,
    mock: pickMockField(tab, sidebarRecord, preferTab),
    // gRPC / WebSocket fields: an edited tab's win, like every field above (card 5258c71d).
    ...(preferTab ? protocolFieldsOf(tab) : {}),
    // A tab never downgrades a saved request's kind: an absent tab.type means
    // "not carried", not "this is HTTP now".
    type: tab.type || sidebarRecord.type || 'http',
  };
}

/**
 * @param {object} options
 * @param {string|number} options.requestId
 * @param {Array} options.collections
 * @param {Array} options.requestTabs
 * @param {string|null} [options.activeRequestTabId]
 * @param {(id: string|number) => Promise<object|null>} [options.fetchFullRecord] cloud full GET
 */
export async function resolveRequestRecord({
  requestId,
  collections,
  requestTabs,
  activeRequestTabId = null,
  fetchFullRecord,
  liveEditorState = null,
}) {
  const sidebar = findSidebarRequest(collections, requestId);
  const tab = findRequestTabForRequest(requestTabs, requestId, activeRequestTabId);
  let merged = mergeRequestSources(sidebar, tab);

  if (fetchFullRecord) {
    const full = await fetchFullRecord(requestId);
    if (full) {
      merged = mergeRequestSources(full, tab);
    }
  }

  if (liveEditorState) {
    merged = mergeRequestSources(merged, {
      ...liveEditorState,
      hasUnsavedChanges: true,
    });
  }

  return merged;
}
